import { Heading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { DownloadIcon } from '@mm/icons';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  selectQAKeyword,
  selectUnansweredQuestions,
} from '@mm/state/selectors/botAnalyticsSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { Moment } from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import {
  Grid,
  GridCell,
  GridHeader,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import { CHATBOT_EDITOR_TRIGGER } from '../../../../../../../../common/constants/routes';
import './KeywordsTriggered.scss';

type Props = {
  startDate: Moment | null;
  endDate: Moment | null;
  dateRangeMode: string | null;
  mode: 'qaKeywords' | 'unansweredQuestions';
};

type DataItem = {
  id: number;
  name: string;
  count: number;
};

type CellProps = {
  item: DataItem;
  orderNumber?: number;
};

const renderKeywordCell = (props: CellProps) => {
  const { item, orderNumber } = props;
  return (
    <GridCell className="keywords-triggered-grid__cell-keyword-name-container">
      <span>
        {orderNumber}.{' '}
        <span className="keywords-triggered-grid__cell-keyword-name">
          {item.name}
        </span>
      </span>
    </GridCell>
  );
};

const renderTriggeredCell = (props: CellProps) => {
  const { item } = props;
  return (
    <GridCell>
      <span>{item.count}</span>
    </GridCell>
  );
};

const cells = (isUnansweredQuestionsMode: boolean) => {
  return [
    {
      label: isUnansweredQuestionsMode ? 'Questions' : 'Keywords',
      keyName: isUnansweredQuestionsMode ? 'questions' : 'keywords',
      Cell: renderKeywordCell,
    },
    {
      label: 'Triggered',
      keyName: 'triggered',
      Cell: renderTriggeredCell,
    },
  ];
};
const gridTemplateColumns = `60% 40%`;

export const KeywordsTriggered = (props: Props) => {
  const { startDate, endDate, dateRangeMode, mode } = props;
  const { history } = useRouter();
  const [isViewAllMode, setIsViewAllMode] = useState(false);
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const questions = useSelector(selectUnansweredQuestions);
  const qaKeywords = useSelector(selectQAKeyword);
  const {
    retrieveUnansweredQuestions,
    retrieveQAKeywords,
    exportUnansweredQuestions,
    exportQAKeywords,
  } = useBotAnalyticsCRUDActions();
  const { openExportModal } = useModalActions();
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const containerRef = useRef<HTMLDivElement>(null);
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const canViewKeywordsTriggered = featureFlags.analyticsBotKeywordsTriggered;
  const canExportKeywordsTriggered = featureFlags.analyticsBotKeywordsExport;

  const isUnansweredQuestionsMode = mode === 'unansweredQuestions';
  const title = isUnansweredQuestionsMode
    ? 'Unanswered Questions'
    : 'Q&A Keywords Triggered';

  const showUpgradeModal = (feature: keyof typeof featureFlags) => {
    const landmineName = isUnansweredQuestionsMode
      ? 'BOT-ANALYTICS-UNANSWERED-QUESTIONS'
      : 'BOT-ANALYTICS-QA-KEYWORDS';
    openUniversalUpgradeLandmineModal({
      landmineName: landmineNames[landmineName],
      feature,
    });
  };

  useEffect(() => {
    if (mode === 'unansweredQuestions') {
      retrieveUnansweredQuestions(
        activeChatbotId,
        startDate?.format('DD-MM-YYYY'),
        endDate?.format('DD-MM-YYYY'),
      );
    } else {
      retrieveQAKeywords(
        activeChatbotId,
        startDate?.format('DD-MM-YYYY'),
        endDate?.format('DD-MM-YYYY'),
      );
    }
  }, [startDate, endDate]);

  const questionsFormat = questions.map(question => {
    return {
      id: question.id,
      name: question.missedText,
      count: question.occurrences,
    };
  });
  const qaKeywordsFormat = qaKeywords.map((keyword, index) => {
    return {
      id: index,
      name: keyword.name,
      count: keyword.firedCount,
    };
  });
  const data: DataItem[] = isUnansweredQuestionsMode
    ? questionsFormat
    : qaKeywordsFormat;

  const filtredData = useMemo(() => {
    if (isViewAllMode || data.length <= 5) {
      return data;
    }
    return data.slice(0, 5);
  }, [data, isViewAllMode]);

  const onRowClick = (index: number) => {
    if (index !== 0 && !canViewKeywordsTriggered) {
      showUpgradeModal('analyticsBotKeywordsTriggered');
    } else if (!isUnansweredQuestionsMode) {
      history.push(CHATBOT_EDITOR_TRIGGER(activeChatbotId));
    }
  };

  const showTeaser = !canViewKeywordsTriggered && filtredData.length > 1;

  const onViewAll = () => {
    if (!showTeaser) {
      setIsViewAllMode(true);
    }
  };

  const onUploadIconClick = () => {
    if (!canExportKeywordsTriggered) {
      showUpgradeModal('analyticsBotKeywordsExport');
    } else {
      const description =
        `We’ll prepare your ${title} export and email you when it’s ready. ` +
        'Enter the email address you’d like us to send your download link to.';

      openExportModal({
        title: 'Export unanswered questions',
        description,
        onExport: (email: string) => {
          if (mode === 'unansweredQuestions') {
            return exportUnansweredQuestions(
              activeChatbotId,
              email,
              startDate?.format('DD-MM-YYYY'),
              endDate?.format('DD-MM-YYYY'),
            );
          }
          return exportQAKeywords(
            activeChatbotId,
            email,
            startDate?.format('DD-MM-YYYY'),
            endDate?.format('DD-MM-YYYY'),
          );
        },
      });
    }
  };

  const className = classnames('keywords-triggered', {
    'keywords-triggered-teaser': showTeaser,
  });

  const containerHeight = containerRef.current?.clientHeight ?? 0;
  const firstRowHeight =
    containerRef.current?.firstElementChild?.clientHeight ?? 0;
  const bottomPx = (containerHeight - firstRowHeight) / 2 - 10;

  return (
    <div className={className}>
      <Grid>
        <GridHeader className="keywords-triggered__title">
          <Heading text={title} size={3} />
          <div className="keywords-triggered__title-actions-container">
            <div className="keywords-triggered__title-date">
              <span className="keywords-triggered__title-date-show">Show:</span>{' '}
              {dateRangeMode ? dateRangeMode : 'Custom date range'}
            </div>
            <div
              className="keywords-triggered__title-upload-icon"
              onClick={onUploadIconClick}
            >
              <DownloadIcon />
            </div>
          </div>
        </GridHeader>
        <GridLabels
          className="keywords-triggered-grid__labels"
          gridTemplateColumns={gridTemplateColumns}
        >
          {cells(isUnansweredQuestionsMode).map(dataItem => (
            <TableLabel
              key={dataItem.keyName ? String(dataItem.keyName) : dataItem.label}
              dataItem={dataItem}
            />
          ))}
        </GridLabels>
        <div
          className="keywords-triggered-grid__rows-container"
          ref={containerRef}
        >
          {filtredData.map((item, index) => (
            <GridRow
              className={classnames({
                'keywords-triggered-grid__row--clickable':
                  !isUnansweredQuestionsMode,
              })}
              key={item.id}
              gridTemplateColumns={gridTemplateColumns}
              onClick={() => onRowClick(index)}
            >
              {cells(isUnansweredQuestionsMode).map(({ Cell, keyName }) => {
                return (
                  Cell && (
                    <Cell item={item} orderNumber={index + 1} key={keyName} />
                  )
                );
              })}
            </GridRow>
          ))}
          {showTeaser && (
            <div
              className="keywords-triggered-grid__upgrade-flag"
              style={{ bottom: `${bottomPx}px` }}
              onClick={() => showUpgradeModal('analyticsBotKeywordsTriggered')}
            >
              <span>Upgrade</span> your plan to view all data
            </div>
          )}
        </div>
        {data.length ? (
          !isViewAllMode &&
          data.length > 5 && (
            <div
              className="keywords-triggered__view-all-button"
              onClick={onViewAll}
            >
              View all
            </div>
          )
        ) : (
          <div className="keywords-triggered__no-items">No items</div>
        )}
      </Grid>
    </div>
  );
};
